import { useEffect, useState } from 'react'
import { fetchCatalogCategories } from '../../hooks/runtime/catalogApi'
import { buildCategoryPathFromTrail } from '../../routing'
import type { CatalogCategory, CatalogProduct } from '../../types'

export const useProductCategoryBreadcrumbs = (productData: CatalogProduct | null) => {
  const [categoryBreadcrumbs, setCategoryBreadcrumbs] = useState<Array<{ name: string; path: string }>>([])

  useEffect(() => {
    if (!productData?._id) {
      setCategoryBreadcrumbs([])
      return
    }

    const categoryIds = Array.from(
      new Set(
        [productData.categoryId, ...(productData.categoryIds ?? [])]
          .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
          .filter(Boolean),
      ),
    )

    if (categoryIds.length === 0) {
      setCategoryBreadcrumbs([])
      return
    }

    const primaryId = (productData.categoryIds?.[0] || productData.categoryId || '').trim()
    const controller = new AbortController()
    let active = true

    const resolveCategoryLabels = async () => {
      try {
        const categories = await fetchCatalogCategories(controller.signal)
        if (!active || controller.signal.aborted) return

        const byId = new Map(categories.map((category) => [category._id, category]))
        const trail: CatalogCategory[] = []
        if (primaryId && byId.has(primaryId)) {
          const visited = new Set<string>()
          let currentId: string | null = primaryId
          let guard = 0
          while (currentId && guard < 20) {
            guard += 1
            if (visited.has(currentId)) break
            visited.add(currentId)
            const category = byId.get(currentId)
            if (!category) break
            trail.unshift(category)
            currentId = category.parentCategoryId
          }
        }

        setCategoryBreadcrumbs(
          trail.map((category, index) => ({
            name: category.name || category.slug,
            path: buildCategoryPathFromTrail(trail.slice(0, index + 1)),
          })),
        )
      } catch {
        if (!active || controller.signal.aborted) return
        setCategoryBreadcrumbs([])
      }
    }

    void resolveCategoryLabels()
    return () => {
      active = false
      controller.abort()
    }
  }, [productData?._id, productData?.categoryId, productData?.categoryIds])

  return categoryBreadcrumbs
}
